import React from 'react';
import { 
  Users, Mail, BarChart3, Star, FileText, PieChart, 
  LayoutDashboard, MessageCircle, Bot, Award, ShieldCheck, HelpCircle
} from 'lucide-react';
import CTASection from '../components/sections/CTASection';

const Features: React.FC = () => {
  const primaryFeatures = [
    {
      icon: <Users className="w-10 h-10 text-primary" />,
      title: "Lead Capture System",
      description: "Convert website visitors into qualified leads with customizable forms and intelligent tracking that integrates with your existing website or CRM.",
      benefits: [
        "Multi-step qualification forms",
        "Automatic lead scoring",
        "Real-time notifications",
        "CRM integration"
      ],
      image: "https://images.pexels.com/photos/8867482/pexels-photo-8867482.jpeg"
    },
    {
      icon: <Mail className="w-10 h-10 text-primary" />,
      title: "Email Marketing Automation",
      description: "Nurture relationships with automated email sequences tailored to each prospect's needs and interests, moving them through your sales funnel.",
      benefits: [
        "Drag-and-drop email builder",
        "Automated nurture sequences",
        "A/B testing capabilities",
        "Performance analytics"
      ],
      image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg"
    },
    {
      icon: <BarChart3 className="w-10 h-10 text-primary" />,
      title: "Performance Analytics",
      description: "Track campaign performance and ROI with comprehensive monthly reporting that helps you optimize your marketing strategy.",
      benefits: [
        "Conversion tracking",
        "Campaign comparison",
        "ROI calculation",
        "Custom report generation"
      ],
      image: "https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg"
    },
    {
      icon: <FileText className="w-10 h-10 text-primary" />,
      title: "Custom Landing Pages",
      description: "Create high-converting landing pages designed specifically for financial services without needing any design or coding skills.",
      benefits: [
        "Industry-specific templates",
        "Mobile optimization",
        "A/B testing",
        "Lead form integration"
      ],
      image: "https://images.pexels.com/photos/8867495/pexels-photo-8867495.jpeg"
    },
    {
      icon: <div className="flex space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-10 h-10 text-primary" />
        ))}
      </div>,
      title: "Review Management",
      description: "Generate and manage 5-star reviews on Google and Yelp to build credibility and attract more high-value clients.",
      benefits: [
        "Automated review requests",
        "Review monitoring",
        "Response management",
        "Review widgets for your website"
      ],
      image: "https://images.pexels.com/photos/8867386/pexels-photo-8867386.jpeg"
    },
    {
      icon: <PieChart className="w-10 h-10 text-primary" />,
      title: "Advanced Campaigns",
      description: "Deploy sophisticated marketing campaigns targeted to specific client segments based on their behaviors and preferences.",
      benefits: [
        "Audience segmentation",
        "Behavioral triggers",
        "Multi-channel campaigns",
        "Performance tracking"
      ],
      image: "https://images.pexels.com/photos/7688159/pexels-photo-7688159.jpeg"
    }
  ];

  const secondaryFeatures = [
    {
      icon: <LayoutDashboard className="w-6 h-6 text-primary" />,
      title: "Intuitive Dashboard",
      description: "Get a clear overview of all your marketing activities and results in one place."
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-primary" />,
      title: "Email Support",
      description: "Get help when you need it with our responsive support team."
    },
    {
      icon: <Bot className="w-6 h-6 text-primary" />,
      title: "AI Content Assistant",
      description: "Generate compliant marketing content with our AI-powered assistant."
    },
    {
      icon: <Award className="w-6 h-6 text-primary" />,
      title: "Compliance Tools",
      description: "Keep your marketing FINRA and SEC compliant with built-in compliance tools."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-primary" />,
      title: "Data Security",
      description: "Enterprise-grade security to keep your client data safe and secure."
    },
    {
      icon: <HelpCircle className="w-6 h-6 text-primary" />,
      title: "Training Resources",
      description: "Access video tutorials and guides to get the most from the platform."
    }
  ];

  const reviewStats = [
    { number: "93%", label: "5-star review rate" },
    { number: "47%", label: "Increase in referrals" },
    { number: "4.8", label: "Average Google rating" },
    { number: "2.3x", label: "More review responses" }
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="section bg-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-6 text-white">
              Powerful Features Designed for Financial Advisors
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Everything you need to attract, convert, and retain high-value clients in one integrated platform.
            </p>
          </div>
        </div>
      </section>

      {/* Primary Features */}
      <section className="section">
        <div className="container-custom">
          <div className="space-y-24">
            {primaryFeatures.map((feature, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="mb-4">{feature.icon}</div>
                  <h2 className="text-3xl font-bold mb-4 text-gray-900">{feature.title}</h2>
                  <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="h-5 w-5 text-secondary mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`rounded-xl overflow-hidden shadow-xl ${
                  index % 2 === 1 ? 'lg:order-1' : ''
                }`}>
                  <img 
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review System Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="flex justify-center space-x-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-12 h-12 text-secondary" />
              ))}
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
              The Power of Five-Star Reviews
            </h2>
            <p className="text-xl text-gray-600">
              Our proprietary review management system helps financial advisors consistently earn and showcase authentic 5-star reviews on Google and Yelp, building trust and attracting high-value clients.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {reviewStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-12">
                <h3 className="text-2xl font-bold mb-6">Our Proven Review System</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-secondary/10 rounded-full p-2">
                      <Star className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold mb-2">Perfect Timing Technology™</h4>
                      <p className="text-gray-600">Our AI-powered system identifies the optimal moment to request reviews, ensuring maximum positive responses.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-secondary/10 rounded-full p-2">
                      <MessageCircle className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold mb-2">Smart Response Templates</h4>
                      <p className="text-gray-600">Compliance-approved response templates help you engage with reviews professionally and promptly.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-secondary/10 rounded-full p-2">
                      <Award className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold mb-2">Review Showcasing</h4>
                      <p className="text-gray-600">Automatically display your best reviews on your website and marketing materials to build trust.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8867384/pexels-photo-8867384.jpeg"
                  alt="Financial advisor receiving positive client reviews"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/50 to-transparent lg:from-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Features */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Additional Platform Features
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Everything you need to run successful marketing campaigns that drive results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {secondaryFeatures.map((feature, index) => (
              <div key={index} className="card p-6">
                <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Features;